import React, { useState, useCallback, useEffect, useRef } from 'react';

export interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'left' | 'center' | 'right';
  side?: 'top' | 'bottom';
  className?: string;
}

const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      trigger,
      children,
      isOpen: controlledOpen,
      onOpenChange,
      align = 'left',
      side = 'bottom',
      className = '',
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;

    const setOpen = useCallback(
      (open: boolean) => {
        if (!isControlled) setInternalOpen(open);
        onOpenChange?.(open);
      },
      [isControlled, onOpenChange],
    );

    const close = useCallback(() => setOpen(false), [setOpen]);

    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (e: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
          close();
        }
      };

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') close();
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, close]);

    const handleTriggerClick = () => setOpen(!isOpen);

    const alignClasses =
      align === 'center'
        ? 'left-1/2 -translate-x-1/2'
        : align === 'right'
          ? 'right-0 left-auto'
          : 'left-0 right-auto';

    const positionClasses =
      side === 'top' ? 'bottom-full mb-2' : 'mt-2';

    return (
      <div
        ref={(node) => {
          (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className={`relative inline-block ${className}`}
      >
        <div role="button" tabIndex={0} onClick={handleTriggerClick} onKeyDown={(e) => e.key === 'Enter' && handleTriggerClick()}>
          {trigger}
        </div>
        {isOpen && (
          <div
            className={`z-dropdown absolute ${positionClasses} ${alignClasses} rounded-xl bg-surface-elevated border border-subtle shadow-lg animate-scale-in`}
          >
            {children}
          </div>
        )}
      </div>
    );
  },
);

Popover.displayName = 'Popover';

export default Popover;
